'use client';

import { c as _c } from "react/compiler-runtime";
import type { SamlSsoSignInAttempt } from '@/utils/github/saml-sso-sign-in';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { REPOSITORY_DATA_SOURCE } from '@/data/installations/constants';
import { getRepositoriesSamlSsoOutcomeEvent } from '@/data/installations/queries';
import { useInstallationRepositories } from '@/data/installations/use-installation-repositories';
import { SAML_SSO_ANALYTICS_EVENTS } from '@/lib/analytics/events';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { clearSamlSsoSignInAttempt, readReturnedSamlSsoSignInAttempt } from '@/utils/github/saml-sso-sign-in';

/**
 * Reports whether "Sign in again" from SAML SSO recovery cleared the block by loading the
 * installation's repositories with the new token, the request GitHub blocked before. It is
 * mounted once for the whole app because a recovery surface disappears when access returns.
 */
export function SamlSsoSignInOutcomeReporter() {
  const $ = _c(2);
  const [attempt] = useState(readReturnedSamlSsoSignInAttempt);
  let t0;
  if ($[0] !== attempt) {
    t0 = attempt ? <ReportSamlSsoSignInOutcome attempt={attempt} /> : null;
    $[0] = attempt;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function ReportSamlSsoSignInOutcome(t0) {
  const $ = _c(11);
  const {
    attempt
  } = t0;
  const repositoriesQuery = useInstallationRepositories(attempt.installationId, REPOSITORY_DATA_SOURCE.GITHUB);
  const isSettled = !repositoriesQuery.isPending && !repositoriesQuery.isFetching;
  const reportedRef = useRef(false);
  let t1;
  if ($[0] !== attempt.analyticsProperties || $[1] !== attempt.installationId || $[2] !== attempt.organizationLogin || $[3] !== attempt.startedAt || $[4] !== isSettled || $[5] !== repositoriesQuery) {
    t1 = () => {
      if (!isSettled || reportedRef.current) {
        return;
      }
      reportedRef.current = true;
      clearSamlSsoSignInAttempt();
      const outcomeEvent = getRepositoriesSamlSsoOutcomeEvent(repositoriesQuery);
      trackClientEvent(outcomeEvent, attempt.installationId.toString(), {
        ...attempt.analyticsProperties,
        trigger: "sign_in_again",
        sign_in_duration_ms: Date.now() - attempt.startedAt
      });
      if (outcomeEvent === SAML_SSO_ANALYTICS_EVENTS.REFRESH_STILL_BLOCKED) {
        toast.error(`GitHub still blocks cubic for ${attempt.organizationLogin ?? "your organization"}`, {
          description: "Start the SSO session first, then sign in again. If that fails, revoke cubic in Authorized GitHub Apps and sign in again.",
          duration: 15000
        });
      }
    };
    $[0] = attempt.analyticsProperties;
    $[1] = attempt.installationId;
    $[2] = attempt.organizationLogin;
    $[3] = attempt.startedAt;
    $[4] = isSettled;
    $[5] = repositoriesQuery;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== attempt || $[8] !== isSettled || $[9] !== repositoriesQuery) {
    t2 = [attempt, isSettled, repositoriesQuery];
    $[7] = attempt;
    $[8] = isSettled;
    $[9] = repositoriesQuery;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  useEffect(t1, t2);
  return null;
}